
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Game } from "@/types/game";
import { uploadGameCover } from "./imageUploadService";

export const createGame = async (gameName: string, coverFile: File | null): Promise<boolean> => {
  try {
    console.log(`Creating game: ${gameName}, with cover file: ${coverFile?.name || 'none'}`);
    
    // First insert the game to get an ID
    const { data: gameData, error: insertError } = await supabase
      .from("games")
      .insert({ name: gameName })
      .select()
      .single();

    if (insertError || !gameData) {
      console.error("Error creating game:", insertError);
      toast({
        title: "Error",
        description: "Could not create game.",
        variant: "destructive",
      });
      return false;
    }

    console.log("Game created successfully with ID:", gameData.id);

    // Upload cover file if exists
    if (coverFile) {
      console.log("Uploading cover file...");
      const coverUrl = await uploadGameCover(gameData.id, coverFile, "");
      console.log("Cover upload result:", coverUrl);
      
      if (coverUrl) {
        console.log("Updating game with cover URL:", coverUrl);
        // Update the game with the cover URL
        const { error: updateError } = await supabase
          .from("games")
          .update({ cover_url: coverUrl })
          .eq("id", gameData.id);

        if (updateError) {
          console.error("Error updating cover URL:", updateError);
          toast({
            title: "Aviso",
            description: "Jogo criado, mas houve um problema ao salvar a imagem de capa.",
            variant: "destructive",
          });
        } else {
          console.log("Game cover URL updated successfully");
        }
      }
    }

    toast({
      title: "Sucesso",
      description: "Jogo criado com sucesso!",
    });
    
    return true;
  } catch (error) {
    console.error("Error creating game:", error);
    toast({
      title: "Erro",
      description: "Ocorreu um erro ao criar o jogo.",
      variant: "destructive",
    });
    return false;
  }
};

export const updateGame = async (
  game: Game, 
  newName: string, 
  coverFile: File | null, 
  coverUrl: string,
  shouldRemoveCover: boolean = false
): Promise<boolean> => {
  try {
    console.log(`Updating game: ${game.id}, new name: ${newName}`);
    console.log(`Current coverUrl: ${coverUrl}`);
    console.log(`New cover file: ${coverFile?.name || 'none'}`);
    console.log(`Should remove cover: ${shouldRemoveCover}`);
    
    // Handle cover image upload if new file is selected
    let finalCoverUrl: string | null = coverUrl;
    
    if (shouldRemoveCover) {
      console.log("Removing cover image entirely");
      finalCoverUrl = null;
    } else if (coverFile) {
      console.log("Uploading new cover file...");
      const newCoverUrl = await uploadGameCover(game.id, coverFile, game.cover_url || '');
      console.log("New cover upload result:", newCoverUrl);
      
      if (newCoverUrl) {
        finalCoverUrl = newCoverUrl;
        console.log("Updated finalCoverUrl to:", finalCoverUrl);
      } else {
        console.error("Failed to upload new cover, keeping existing URL");
        // Keep the existing URL from the database if upload failed
        finalCoverUrl = game.cover_url || null;
      }
    } else {
      // No new file selected, keep existing URL from database
      finalCoverUrl = game.cover_url || null;
    }

    console.log("Final cover URL for update:", finalCoverUrl);
    
    // Update the game data
    const { error: updateError } = await supabase
      .from("games")
      .update({
        name: newName,
        cover_url: finalCoverUrl
      })
      .eq("id", game.id);

    if (updateError) {
      console.error("Error updating game:", updateError);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o jogo.",
        variant: "destructive",
      });
      return false;
    }

    console.log("Game updated successfully");
    toast({
      title: "Sucesso",
      description: "Jogo atualizado com sucesso!",
    });
    
    return true;
  } catch (error) {
    console.error("Error updating game:", error);
    toast({
      title: "Erro",
      description: "Ocorreu um erro ao atualizar o jogo.",
      variant: "destructive",
    });
    return false;
  }
};

// Keep the repairGameCovers function as it is
export const repairGameCovers = async (): Promise<void> => {
  try {
    console.log("Starting repair process for game covers...");
    
    // Get all games
    const { data: games, error: gamesError } = await supabase
      .from("games")
      .select("*");
      
    if (gamesError || !games) {
      console.error("Error fetching games for repair:", gamesError);
      return;
    }
    
    console.log(`Found ${games.length} games to check`);
    let repaired = 0;
    
    // Process each game
    for (const game of games) {
      try {
        // If game already has a cover URL but it doesn't include the proper storage path, fix it
        if (game.cover_url && !game.cover_url.includes('storage/v1/object/public/games')) {
          console.log(`Game ${game.id}: has malformed URL: ${game.cover_url}, fixing...`);
          
          // Try to fix the URL format if it's just missing the storage path
          const fileName = game.cover_url.split('/').pop();
          if (fileName) {
            const fixedUrl = `https://vcjgzqjktnpdjeunnhme.supabase.co/storage/v1/object/public/games/${fileName}`;
            
            // Update with corrected URL
            const { error: updateError } = await supabase
              .from("games")
              .update({ cover_url: fixedUrl })
              .eq("id", game.id);
              
            if (!updateError) {
              console.log(`Fixed malformed URL for game ${game.id}: ${fixedUrl}`);
              repaired++;
              continue;
            }
          }
        }
        
        // If game still doesn't have a valid cover URL, check storage
        if (!game.cover_url || !game.cover_url.includes('storage/v1/object/public/games')) {
          console.log(`Game ${game.id}: missing cover URL, checking storage...`);
          
          // First try the conventional name pattern
          const { data: fileData } = await supabase.storage
            .from("games")
            .list('', {
              search: `game-${game.id}`
            });
            
          if (fileData && fileData.length > 0) {
            // Found a matching file
            const fileName = fileData[0].name;
            console.log(`Found file for game ${game.id}: ${fileName}`);
            
            // Get public URL
            const { data: urlData } = supabase.storage
              .from("games")
              .getPublicUrl(fileName);
              
            // Update game with URL
            const { error: updateError } = await supabase
              .from("games")
              .update({ cover_url: urlData.publicUrl })
              .eq("id", game.id);
              
            if (updateError) {
              console.error(`Error updating cover URL for game ${game.id}:`, updateError);
            } else {
              console.log(`Fixed cover URL for game ${game.id}: ${urlData.publicUrl}`);
              repaired++;
            }
          } else {
            console.log(`No matching files found for game ${game.id}`);
          }
        } else {
          console.log(`Game ${game.id}: already has valid cover URL: ${game.cover_url}`);
        }
      } catch (gameError) {
        console.error(`Error processing game ${game.id}:`, gameError);
      }
    }
    
    console.log(`Game cover repair process completed. Fixed ${repaired} games.`);
  } catch (error) {
    console.error("Error in repairGameCovers:", error);
  }
};
